import type {
	API_UpdateCustomerRequest,
	CustomerStatus,
	Gender,
	IdType,
} from "@pilatestime/shared-types";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	fetchCustomerByIdQueryOptions,
	useUpdateCustomer,
} from "@/api/customers";
import { useApiParameters } from "@/hooks/useApiParameters";
import { CustomerRegistration } from "@/routes/_auth/app/_app/org/$orgId/customers/-components/CustomerRegistration";
import { validateCPF } from "@/utils/cpfValidation";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/customers/$customerId/edit",
)({
	loader: async ({ context, params }) => {
		const { userId, token } = useApiParameters(
			context.userId,
			await context.get_token(),
		);
		const { orgId, customerId } = params;

		// Preload customer data
		context.queryClient.ensureQueryData(
			fetchCustomerByIdQueryOptions(userId, token, orgId, customerId),
		);

		return { userId, token, orgId, customerId };
	},
	component: EditCustomerPage,
});

function EditCustomerPage() {
	const { t } = useTranslation();
	const { orgId, customerId } = Route.useParams();
	const { userId, token } = Route.useLoaderData();
	const navigate = useNavigate();

	// Get customer data
	const { data: customer } = useSuspenseQuery(
		fetchCustomerByIdQueryOptions(userId, token, orgId, customerId),
	);

	// Form state
	const [formData, setFormData] = useState<API_UpdateCustomerRequest>({});
	const [errors, setErrors] = useState<Record<string, string>>({});

	// Update customer mutation
	const updateCustomerMutation = useUpdateCustomer();

	// Initialize form data when customer data loads
	useEffect(() => {
		if (customer) {
			setFormData({
				firstName: customer.firstName,
				lastName: customer.lastName,
				email: customer.email || "",
				phone: customer.phone || "",
				dateOfBirth: customer.dateOfBirth
					? new Date(customer.dateOfBirth).toISOString().split("T")[0]
					: "",
				gender: customer.gender,
				idType: customer.idType || undefined,
				idNumber: customer.idNumber || "",
				address: customer.address || "",
				city: customer.city || "",
				state: customer.state || "",
				postalCode: customer.postalCode || "",
				country: customer.country || "",
				profession: customer.profession || "",
				emergencyContactName: customer.emergencyContactName || "",
				emergencyContactPhone: customer.emergencyContactPhone || "",
				notes: customer.notes || "",
				status: customer.status,
			});
		}
	}, [customer]);

	// Handle form field changes
	const handleFieldChange = useCallback(
		(
			field: keyof API_UpdateCustomerRequest | "status",
			value: string | Gender | CustomerStatus | IdType | undefined,
		) => {
			// Convert empty strings to undefined for optional fields
			let processedValue = value;
			if (typeof value === "string" && value.trim() === "") {
				processedValue = undefined;
			}

			setFormData((prev) => ({
				...prev,
				[field]: processedValue,
			}));

			// Clear error for this field
			setErrors((prev) => {
				if (prev[field]) {
					const newErrors = { ...prev };
					delete newErrors[field];
					return newErrors;
				}
				return prev;
			});
		},
		[],
	);

	// Validate form
	const validateForm = (): boolean => {
		const newErrors: Record<string, string> = {};

		if (!formData.firstName?.trim()) {
			newErrors.firstName = t("validation.required");
		}

		if (!formData.lastName?.trim()) {
			newErrors.lastName = t("validation.required");
		}

		if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
			newErrors.email = t("validation.invalidEmail");
		}

		if (
			formData.dateOfBirth &&
			Number.isNaN(new Date(formData.dateOfBirth).getTime())
		) {
			newErrors.dateOfBirth = t("validation.invalidDate");
		}

		// CPF validation when idType is CPF
		if (formData.idType === "CPF" && formData.idNumber) {
			const cpfError = validateCPF(formData.idNumber, t);
			if (cpfError) {
				newErrors.idNumber = cpfError;
			}
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	// Handle form submission
	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!validateForm()) {
			// Show validation error - this is not handled by mutation
			return;
		}

		try {
			// Clean up form data - remove empty strings for optional fields
			const cleanedData = { ...formData };
			for (const [key, value] of Object.entries(cleanedData)) {
				if (typeof value === "string" && value.trim() === "") {
					delete cleanedData[key as keyof API_UpdateCustomerRequest];
				}
			}

			await updateCustomerMutation.mutateAsync({
				token,
				organizationId: orgId,
				customerId,
				data: cleanedData,
			});

			// Success toast is handled by the mutation hook
			navigate({
				to: "/app/org/$orgId/customers/$customerId",
				params: { orgId, customerId },
			});
		} catch (error) {
			// Error toast is handled by the mutation hook
			console.error("Error updating customer:", error);
		}
	};

	// Handle back navigation
	const handleBack = () => {
		navigate({
			to: "/app/org/$orgId/customers/$customerId",
			params: { orgId, customerId },
		});
	};

	return (
		<div className="h-full flex flex-col space-y-6">
			{/* Form */}
			<div className="h-full overflow-auto">
				<CustomerRegistration
					formData={formData}
					errors={errors}
					onFieldChange={handleFieldChange}
					onSubmit={handleSubmit}
					onCancel={handleBack}
					isSubmitting={updateCustomerMutation.isPending}
					mode="edit"
				/>
			</div>
		</div>
	);
}
